import { Component, HostListener, inject, OnInit, signal, PLATFORM_ID, Inject, ChangeDetectionStrategy, computed } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BasicsConstance } from '../../../core/models/Enums/basics-constance';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { LanguageService } from '../../../core/services/language/language.service';
import { CookieService } from 'ngx-cookie-service';
import { AuthService } from '../../../core/auth/auth.service';
import { HeaderNotificationComponent } from './notification/notification.component';
import { HeaderSidebarComponent } from './sidebar/sidebar.component';
import { HttpService } from '../../../core/data-access/http/http.service';
import { PermissionService } from '../../../core/permissions/permission.service';
import { NotificationService } from '../../../core/services/notification/notification.service';
import { PERMISSIONS } from '../../../core/permissions/permission.constants';

@Component({
  selector: 'app-header',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    RouterModule,
    CommonModule,
    HeaderNotificationComponent,
    HeaderSidebarComponent,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit {
  private _translate = inject(TranslateService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private httpService = inject(HttpService);
  private languageService = inject(LanguageService);
  private permissionService = inject(PermissionService);
  private notificationService = inject(NotificationService);

  // State Signals (Computed from AuthService)
  language = signal<string>('ar');
  isAuthenticated = this.authService.isAuthenticated;
  
  userName = computed(() => {
    const profile = this.authService.userProfile();
    if (!profile) return '';
    return this.language() === 'ar' ? profile.fullNameAr : profile.fullNameEn;
  });

  userAvatar = computed(() => {
    return this.authService.userProfile()?.profileImage || 'images/Avatar.png';
  });
  
  // UI State Signals
  showUserMenu = signal<boolean>(false);
  showNotificationDropdown = signal<boolean>(false);
  isMenuOpen = signal<boolean>(false);
  isExpanded = signal<boolean>(false);
  isScrolled = signal<boolean>(false);
  openDropdownId = signal<string | null>(null);

  // Constants
  protected readonly PERMISSIONS = PERMISSIONS;

  unreadCount = this.notificationService.unreadCount;

  readonly dropdownItems: Array<{
    routerLink: any;
    translationKey: string;
    requiredPermission?: bigint[];
  }> = [
    { routerLink: ['/prosecution/aboutProsecution'], translationKey: 'Home.info' },
    { routerLink: ['/prosecution/current-Secretary'], translationKey: 'Home.secratry' },
    { routerLink: ['/prosecution/AllPresidents'], translationKey: 'Home.HeadOfAdministratives' },
    { routerLink: ['/prosecution/President'], translationKey: 'Home.HeadOfAdministrative' },
  ];

  readonly navigationItems: Array<{
    routerLink: any;
    translationKey: string;
    requiredPermission?: bigint[];
  }> = [
    { routerLink: ['/prosecution/aboutProsecution'], translationKey: 'Home.ProjectName' },
    { routerLink: ['news', 'allNews'], translationKey: 'Home.NewsAndEvents' },
    { routerLink: ['services', 'allServices'], translationKey: 'Home.Services' },
    { routerLink: ['digitalLibrary'], translationKey: 'Home.digitalLibrary' },
    { routerLink: ['contactUs'], translationKey: 'Home.ContactUs' },
  ];

  constructor(@Inject(PLATFORM_ID) private platformId: object) {
    this.languageService.currentLanguage$.subscribe(lang => {
      this.language.set(lang);
      this.setLanguageAttributes(lang);
    });
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (isPlatformBrowser(this.platformId)) {
      this.isScrolled.set(window.scrollY > 20);
    }
  }

  ngOnInit(): void {
    // Auth state is now automatically managed by signals via AuthService
  }

  hasPermission(requiredPermissions: bigint[]): boolean {
    if (!requiredPermissions || requiredPermissions.length === 0) return true;
    return this.permissionService.hasAnyPermission(...requiredPermissions);
  }

  changeLang(): void {
    const newLang = this._translate.currentLang === 'ar' ? 'en' : 'ar';
    localStorage.setItem(BasicsConstance.LANG, newLang);
    this._translate.use(newLang);
    this.setLanguageAttributes(newLang);
    this.language.set(newLang);
    this.httpService.clearCache();
    window.location.reload();
  }

  private setLanguageAttributes(lang: string): void {
    if (isPlatformBrowser(this.platformId)) {
      document.documentElement.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr');
      document.documentElement.setAttribute('lang', lang);
    }
  }

  // Notification Toggles
  toggleNotificationDropdown(): void {
    this.showNotificationDropdown.update(v => !v);
  }

  closeNotificationDropdown(): void {
    this.showNotificationDropdown.set(false);
  }

  // User Menu Toggles
  toggleUserMenu(): void {
    this.showUserMenu.update(v => !v);
  }

  closeUserMenu(): void {
    this.showUserMenu.set(false);
  }

  // Mobile Menu Toggles
  toggleMenu(): void {
    this.isMenuOpen.update(v => !v);
  }

  // Dropdown Toggles
  toggleDropdown(): void {
    this.isExpanded.update(v => !v);
  }

  toggleNavigationDropdown(itemId: string): void {
    if (this.openDropdownId() === itemId) {
      this.openDropdownId.set(null);
    } else {
      this.openDropdownId.set(itemId);
    }
  }

  closeNavigationDropdown(): void {
    this.openDropdownId.set(null);
  }

  hasDropdown(item: any): boolean {
    return item.translationKey === 'Home.ProjectName';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.header__nav-dropdown') && !target.closest('.header__nav-link')) {
      this.closeNavigationDropdown();
    }
    if (
      !target.closest('.header__user-section') &&
      !target.closest('.header__user-dropdown') &&
      !target.closest('.header__user-trigger') &&
      !target.closest('.sidebar__user') &&
      !target.closest('.sidebar__user-button') &&
      !target.closest('.sidebar__submenu')
    ) {
      this.closeUserMenu();
    }
  }

  logout(): void {
    this.closeUserMenu();
    this.authService.logout();
  }
}
